import React from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    ContentSection,
    Grid,
    GridColumn,
    GridRow,
    LabeledBadge,
    LabeledDate,
    LabeledText
} from "sprint-common-web";

import { OnPremOrderSource } from "../../../../common/api/orders-api/on-prem-order/GetOnPremOrderListResponse";
import { LabeledTrailerInfoBadge } from "../../../../common/components/trailer-info/LabeledTrailerInfoBadge";
import { SprintAccessRight } from "../../../../core/security/SprintAccessRight";
import { useAccessVerifier } from "../../../../core/security/useAccessVerifier";
import { stringifyOrderResolutionDimensions } from "../../../common/types/OrderResolutionDimensions";
import { resolveOnPremOrderSourceBadgeColor } from "../../common/utils/resolveOnPremOrderSourceBadgeColor";
import { useGetOnPremOrderDashboard } from "../hooks/useGetOnPremOrderDashboard";
import { UpdateOnPremOrderStatusDetailsButton } from "./UpdateOnPremOrderStatusDetailsButton";

interface OnPremOrderDetailsSectionProps {
    minHeight: number;
}

export const OnPremOrderDetailsSection: React.VFC<OnPremOrderDetailsSectionProps> = ({
    minHeight
}: OnPremOrderDetailsSectionProps) => {
    const { isAuthorized } = useAccessVerifier();
    const { isLoading, onPremOrderDetails } = useGetOnPremOrderDashboard();

    const onPremOrderActionButtons: ButtonRenderProps<ButtonProps>[] = [
        {
            render: UpdateOnPremOrderStatusDetailsButton,
            disabled: !isAuthorized([SprintAccessRight.AssetsReadWrite])
        }
    ];

    const { targetResolution } = onPremOrderDetails;
    const targetResolutionText = targetResolution ? stringifyOrderResolutionDimensions(targetResolution) : "-";

    return (
        <ContentSection
            header="Details"
            style={{ minHeight, flex: 1 }}
            isLoading={isLoading}
            buttons={onPremOrderActionButtons}
            testId={"onprem-details-section"}
        >
            <Grid>
                <GridRow>
                    <GridColumn lg={6}>
                        <LabeledText
                            label="PO number"
                            testId="onprem-order-po-number"
                            value={onPremOrderDetails.pONumber}
                        />
                        <LabeledText
                            label="Content"
                            testId="onprem-order-content"
                            value={onPremOrderDetails.contentTitle}
                            maxLength={50}
                        />
                        <LabeledText
                            label="Airline"
                            testId="onprem-order-airline"
                            value={onPremOrderDetails.airline?.name}
                        />
                        {!!onPremOrderDetails.isTrailer && <LabeledTrailerInfoBadge />}
                        <LabeledText
                            label="System"
                            testId="onprem-order-system"
                            value={onPremOrderDetails.system?.name}
                        />
                    </GridColumn>
                    <GridColumn lg={6}>
                        <LabeledText
                            label="Modified By"
                            testId="onprem-order-modifiedby"
                            value={onPremOrderDetails.modifiedBy}
                        />
                        <LabeledDate
                            label="Start date"
                            testId="onprem-order-startDate"
                            value={new Date(onPremOrderDetails.startDate)}
                        />
                        <LabeledText
                            label="Target resolution"
                            testId="onprem-order-target-resolution"
                            value={targetResolutionText}
                        />
                        <LabeledBadge
                            label="Source"
                            value={OnPremOrderSource[onPremOrderDetails.source]}
                            testId="info-field"
                            badgeSize={14}
                            color={resolveOnPremOrderSourceBadgeColor(onPremOrderDetails.source)}
                        />
                    </GridColumn>
                </GridRow>
            </Grid>
        </ContentSection>
    );
};
